import { Dialog, DialogContent } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { File, FileText, Image as ImageIcon, Database, Loader2 } from 'lucide-react'
import { useState, useEffect } from 'react';

interface DataNodeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  filename: string
  content: string
  mimeType?: string
  metadata?: {
    type?: string
    isUrl?: boolean
    filename?: string
    mimeType?: string
    status?: string
    output?: string
    error?: string
    processed?: boolean
  }
}

export function DataNodeDialog({
  open,
  onOpenChange,
  filename,
  content,
  mimeType,
  metadata
}: DataNodeDialogProps) {
  // Debug log for props
  // console.log('[DataNodeDialog] Props:', {
  //   filename,
  //   mimeType,
  //   metadata: metadata ? {
  //     ...metadata,
  //     output: metadata.output ? metadata.output.substring(0, 100) + '...' : undefined,
  //     status: metadata.status
  //   } : undefined,
  //   content: content?.substring(0, 100) + '...'
  // });

  // Helper function to get file icon based on mime type
  const getFileIcon = () => {
    if (mimeType?.startsWith('image/')) {
      return <ImageIcon className="h-5 w-5 text-muted-foreground" />;
    } else if (mimeType?.startsWith('text/')) {
      return <FileText className="h-5 w-5 text-muted-foreground" />;
    } else if (mimeType?.startsWith('application/')) {
      return <Database className="h-5 w-5 text-muted-foreground" />;
    }
    return <File className="h-5 w-5 text-muted-foreground" />;
  };

  // Track dialog state for animations
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (open) {
      setIsOpen(true);
    }
  }, [open]);

  return (
    <Dialog 
      open={open} 
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          // Add a small delay to let animations complete
          setTimeout(() => setIsOpen(false), 150);
        }
        onOpenChange(newOpen);
      }}
    >
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <div className="flex flex-col space-y-4">
          {/* Header with file info */}
          <div className="flex items-center gap-2">
            <div className="h-5 w-5">
              {getFileIcon()}
            </div>
            <span className="text-sm font-medium">
              {filename}
            </span>
            {metadata?.status && (
              <span className={cn(
                "text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1",
                metadata.status === 'processing' && "bg-blue-100 text-blue-700",
                metadata.status === 'complete' && "bg-green-100 text-green-700",
                metadata.status === 'error' && "bg-red-100 text-red-700"
              )}>
                {metadata.status === 'processing' && (
                  <Loader2 className="h-3 w-3 animate-spin" />
                )}
                {metadata.status}
              </span>
            )}
          </div>

          {/* Content sections */}
          <div className="space-y-6">
            {/* Processing indicator */}
            {metadata?.status === 'processing' && (
              <div className="flex items-center justify-center p-4 bg-muted rounded-lg">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Processing file...</span>
              </div>
            )}

            {/* Processed Output - Show first if available */}
            {metadata?.output && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Processed Output</h3>
                <div className="p-4 rounded-lg bg-muted whitespace-pre-wrap text-sm">
                  {metadata.output}
                </div>
              </div>
            )}

            {/* Original Content */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Original Content</h3>
              <div className="p-4 rounded-lg bg-muted whitespace-pre-wrap text-sm">
                {metadata?.isUrl ? (
                  <a href={content} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {content}
                  </a>
                ) : (
                  content
                )}
              </div>
            </div>
          </div>

          {/* Debug Info */}
          {import.meta.env.DEV && (
            <div className="mt-4 pt-4 border-t border-border">
              <details className="text-xs text-muted-foreground" open>
                <summary>Debug Info</summary>
                <pre className="mt-2 p-2 bg-muted rounded">
                  {JSON.stringify({
                    filename,
                    mimeType,
                    metadata,
                    contentLength: content?.length
                  }, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
